import { useState, useEffect, useContext, useRef } from 'react';
import domtoimage from 'dom-to-image';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Autocomplete,
    Chip,
    createFilterOptions,
    Typography,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Divider,
    CircularProgress
} from '@mui/material';
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatListBulleted,
    FormatListNumbered,
    FormatColorText,
    BorderColor,
    FormatSize,
    Code,
    Undo,
    Redo,
    Link as LinkIcon,
    LinkOff,
    DriveFileRenameOutline,
    Close as CloseIcon
} from '@mui/icons-material';
import {
    usePopupState,
    bindTrigger,
    bindMenu,
} from 'material-ui-popup-state/hooks';
import { toast } from 'react-toastify';
import ColorPicker from './ColorPicker';
import EmailDraftService from '../services/EmailDraftService';
import { AuthContext } from '../utils/AuthContext';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension, Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';

const filter = createFilterOptions({
    limit: 20
});

const FontSize = Extension.create({
    name: 'fontSize',
    addOptions() {
        return {
            types: ['textStyle'],
        };
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: element => element.style.fontSize.replace(/['"]+/g, ''),
                        renderHTML: attributes => {
                            if (!attributes.fontSize) {
                                return {};
                            }
                            return {
                                style: `font-size: ${attributes.fontSize}`,
                            };
                        },
                    },
                },
            },
        ];
    },
    addCommands() {
        return {
            setFontSize: (fontSize) => ({ chain }) => {
                return chain()
                    .setMark('textStyle', { fontSize })
                    .run();
            },
            unsetFontSize: () => ({ chain }) => {
                return chain()
                    .setMark('textStyle', { fontSize: null })
                    .removeEmptyTextStyle()
                    .run();
            },
        };
    },
});

const FontSizePicker = ({ editor }) => {
    const popupState = usePopupState({
        variant: 'popover',
        popupId: 'font-size-picker',
    });

    const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 32, 48];

    return (
        <Box>
            <Tooltip title="Font Size">
                <IconButton size="small" {...bindTrigger(popupState)} color={editor.getAttributes('textStyle').fontSize ? 'primary' : 'default'}>
                    <FormatSize />
                </IconButton>
            </Tooltip>
            <Menu {...bindMenu(popupState)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                {fontSizes.map((size) => (
                    <MenuItem
                        key={size}
                        onClick={() => {
                            editor.chain().focus().setFontSize(`${size}px`).run();
                            popupState.close();
                        }}
                        selected={editor.getAttributes('textStyle').fontSize === `${size}px`}
                    >
                        <span>{size}</span>
                    </MenuItem>
                ))}
                <Divider />
                <MenuItem
                    onClick={() => {
                        editor.chain().focus().unsetFontSize().run();
                        popupState.close();
                    }}
                >
                    Default
                </MenuItem>
            </Menu>
        </Box>
    );
};

const PlaceholderNode = Node.create({
    name: 'placeholderNode',
    group: 'block',
    atom: true, // Treat as a single unit, prevents Tiptap from parsing internal HTML
    selectable: true,
    draggable: true,

    addAttributes() {
        return {
            label: {
                default: null,
                parseHTML: element => element.getAttribute('label'),
                renderHTML: attributes => {
                    if (!attributes.label) {
                        return {};
                    }
                    return {
                        label: attributes.label,
                    };
                },
            },
            version: {
                default: 0,
            }
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-placeholder]',
            },
            {
                tag: 'span[data-placeholder]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes, { 'data-placeholder': '' })];
    },

    addNodeView() {
        return ({ node }) => {
            const container = document.createElement('div');
            // container.style.border = '2px dashed #673ab7';
            // container.style.borderRadius = '8px';
            // container.style.margin = '10px 0';
            // container.style.padding = '10px';
            // container.style.backgroundColor = '#f9f9ff';
            container.style.position = 'relative';

            // const label = document.createElement('div');
            // label.innerText = node.attrs.label;
            // label.style.fontSize = '12px';
            // label.style.fontWeight = 'bold';
            // label.style.color = '#673ab7';
            // label.style.marginBottom = '5px';
            // container.appendChild(label);

            const content = document.createElement('div');
            content.innerHTML = getLiveTableHTML(node.attrs.label);
            // Ensure internal tables don't overflow the editor
            content.style.overflowX = 'auto';
            // content.style.maxHeight = '300px';
            content.style.overflowY = 'auto';
            container.appendChild(content);

            return {
                dom: container,
            };
        };
    },

    addCommands() {
        return {
            setPlaceholder: (label) => ({ chain }) => {
                return chain()
                    .insertContent({
                        type: this.name,
                        attrs: { label },
                    })
                    .run();
            },
        };
    },

    addInputRules() {
        return [
            {
                find: /\[(Quotations|Table of benefits)\]\s$/,
                handler: ({ state, range, match }) => {
                    const { tr } = state;
                    const label = match[1];

                    if (label) {
                        tr.replaceWith(range.from, range.to, this.type.create({ label }));
                    }
                },
            },
        ];
    },
});

const PlaceholderPicker = ({ editor }) => {
    const popupState = usePopupState({
        variant: 'popover',
        popupId: 'placeholder-picker',
    });

    const placeholders = [
        { label: 'Quotations', value: 'Quotations' },
        { label: 'Table of benefits', value: 'Table of benefits' },
    ];

    return (
        <Box>
            <Tooltip title="Insert Custom Object">
                <IconButton size="small" {...bindTrigger(popupState)}>
                    <Code />
                </IconButton>
            </Tooltip>
            <Menu {...bindMenu(popupState)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                {placeholders.map((p) => (
                    <MenuItem
                        key={p.value}
                        onClick={() => {
                            editor.commands.setPlaceholder(p.value);
                            popupState.close();
                        }}
                    >
                        {p.label}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

const MenuBar = ({ editor }) => {
    if (!editor) {
        return null;
    }

    const setLink = () => {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);

        if (url === null) {
            return;
        }

        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }

        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    return (
        <Box sx={{
            border: '1px solid #e0e0e0',
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            p: 0.5,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: '#fff'
        }}>
            <Tooltip title="Bold">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    color={editor.isActive('bold') ? 'primary' : 'default'}
                >
                    <FormatBold />
                </IconButton>
            </Tooltip>
            <Tooltip title="Italic">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    color={editor.isActive('italic') ? 'primary' : 'default'}
                >
                    <FormatItalic />
                </IconButton>
            </Tooltip>
            <Tooltip title="Underline">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    color={editor.isActive('underline') ? 'primary' : 'default'}
                >
                    <FormatUnderlined />
                </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <FontSizePicker editor={editor} />

            <PlaceholderPicker editor={editor} />

            <ColorPicker
                title="Text Color"
                type="text"
                icon={<FormatColorText />}
                activeColor={editor.getAttributes('textStyle').color}
                onSelect={(color) => editor.chain().focus().setColor(color).run()}
                onClear={() => editor.chain().focus().unsetColor().run()}
            />

            <ColorPicker
                title="Highlight"
                type="highlight"
                icon={<DriveFileRenameOutline />}
                activeColor={editor.getAttributes('highlight').color}
                onSelect={(color) => editor.chain().focus().toggleHighlight({ color }).run()}
                onClear={() => editor.chain().focus().unsetHighlight().run()}
            />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Tooltip title="Bullet List">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    color={editor.isActive('bulletList') ? 'primary' : 'default'}
                >
                    <FormatListBulleted />
                </IconButton>
            </Tooltip>
            <Tooltip title="Numbered List">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    color={editor.isActive('orderedList') ? 'primary' : 'default'}
                >
                    <FormatListNumbered />
                </IconButton>
            </Tooltip>

            <Tooltip title="Set Link">
                <IconButton
                    size="small"
                    onClick={setLink}
                    color={editor.isActive('link') ? 'primary' : 'default'}
                >
                    <LinkIcon />
                </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Tooltip title="Undo">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                >
                    <Undo />
                </IconButton>
            </Tooltip>
            <Tooltip title="Redo">
                <IconButton
                    size="small"
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                >
                    <Redo />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// const getLiveTableImage = async (label) => {
//     let containerId = '';
//     if (label === 'Quotations') containerId = 'quotation-preview-container';
//     if (label === 'Table of benefits') containerId = 'tob-table-container';

//     const node = document.getElementById(containerId);
//     if (!node) return null;

//     // Capture exactly what is displayed in the browser
//     try {
//         // Find header cells to determine if we should adjust width (reference to CopyImageButton logic)
//         const headerCells = node.querySelectorAll('thead [role="columnheader"], thead th, .MuiTableHead-root .MuiTableCell-root');
//         const originalWidth = node.style.width;

//         // If it's a small table, we might have set it to auto in the reference
//         // but for readability of wide tables, we want to maintain its current visible width

//         const dataUrl = await domtoimage.toPng(node, { 
//             bgcolor: '#ffffff',
//             // Increase quality/scale for better readability
//             quality: 1,
//             style: {
//                 'transform': 'scale(1)',
//                 'transform-origin': 'top left'
//             }
//         });

//         return dataUrl.split(',')[1]; // Return just the base64 part
//     } catch (error) {
//         console.error('Error capturing image:', error);
//         return null;
//     }
// };

const buildEmailReadyClone = (containerId) => {
    const container = document.getElementById(containerId);
    if (!container) return null;

    const clone = container.cloneNode(true);

    // Remove unwanted elements
    const selectorsToRemove = [
        'button', 'input', '.MuiChip-root', '.MuiIconButton-root', '.MuiBadge-badge',
        'svg', '.MuiInputBase-root', '.dnd-context', '.MuiInput-root', '.MuiAutocomplete-root',
        '.remove-in-email'
    ];
    clone.querySelectorAll(selectorsToRemove.join(',')).forEach(el => el.remove());

    // Update styles and visibility
    clone.querySelectorAll('*').forEach(el => {
        // Show elements intended for email
        if (el.classList.contains('show-in-email')) {
            el.style.display = 'block';
        }

        // Clean up "Click to edit" without replacing innerHTML of parents
        if (el.children.length === 0 && el.textContent.includes('Click to edit')) {
            el.textContent = el.textContent.replace('Click to edit', '');
        }

        // Ensure tables look like tables in emails
        if (el.tagName === 'TABLE') {
            el.style.borderCollapse = 'collapse';
            el.style.width = '100%';
            // We remove table-level borders to allow individual row/cell border control via TD/TH
            el.style.border = 'none';
            el.setAttribute('border', '0');
        }
        if (el.tagName === 'TD' || el.tagName === 'TH') {
            const isNested = el.parentElement?.closest('td, th');
            const hasNoBorder = isNested ||
                el.classList.contains('remove-border-in-email') ||
                (el.parentElement && el.parentElement.classList.contains('remove-border-in-email'));

            el.style.border = hasNoBorder ? 'none' : '1px solid #d0d7e2';
            el.style.padding = isNested ? '0px' : '6px';
            // The editable TOB table outlines whichever cells are selected for formatting --
            // that outline is an editing affordance, not table content, so it never belongs in the email.
            el.style.boxShadow = 'none';

            if (el.classList.contains('remove-border-top-on-email') || (el.parentElement && el.parentElement.classList.contains('remove-border-top-on-email'))) {
                el.style.borderTop = 'none';
                el.style.paddingTop = '0px';
            }

            if (el.classList.contains('remove-border-bottom-on-email') || (el.parentElement && el.parentElement.classList.contains('remove-border-bottom-on-email'))) {
                el.style.borderBottom = 'none';
                el.style.paddingBottom = '0px';
            }
        }
    });

    return clone;
};

// The tables fill the page on screen, so with only 1 or 2 plans each plan column would
// stretch across the whole image. While capturing, plan columns are held to this width.
const EMAIL_PLAN_COLUMN_WIDTH = 200;

// Narrows each top-level table (first column + plans) so no plan column is wider than
// EMAIL_PLAN_COLUMN_WIDTH; tables with enough plans to fill the page are left as they are.
// allCapped is true only when every table was narrowed.
const capPlanColumnWidths = (node) => {
    const tables = [...node.querySelectorAll('table')].filter(table =>
        !table.parentElement.closest('table') && table.querySelector(':scope > colgroup')
    );
    const originalWidths = tables.map(table => table.style.width);
    let allCapped = tables.length > 0;

    tables.forEach(table => {
        const cols = table.querySelectorAll(':scope > colgroup > col');
        const firstColumnWidth = parseFloat(cols[0]?.style.width) || 0;
        const maxWidth = firstColumnWidth + Math.max(0, cols.length - 1) * EMAIL_PLAN_COLUMN_WIDTH;
        if (table.offsetWidth > maxWidth) {
            table.style.width = `${maxWidth}px`;
        } else {
            allCapped = false;
        }
    });

    return {
        allCapped,
        restore: () => tables.forEach((table, i) => { table.style.width = originalWidths[i]; }),
    };
};

const getLiveTableImage = async (label, scale = 3) => {
    let containerId = '';
    if (label === 'Quotations') containerId = 'quotation-preview-container';
    if (label === 'Table of benefits') containerId = 'tob-table-container';

    const node = document.getElementById(containerId);
    if (!node) return null;

    const scrollEls = [node, ...node.querySelectorAll('*')].filter((el) => {
        const cs = window.getComputedStyle(el);
        return (
            cs.overflowY === 'auto' || cs.overflowY === 'scroll' ||
            cs.overflowX === 'auto' || cs.overflowX === 'scroll'
        );
    });

    const originalStyles = scrollEls.map((el) => ({
        el,
        height: el.style.height,
        maxHeight: el.style.maxHeight,
        overflow: el.style.overflow,
        overflowX: el.style.overflowX,
        overflowY: el.style.overflowY,
    }));

    scrollEls.forEach((el) => {
        el.style.height = 'auto';
        el.style.maxHeight = 'none';
        el.style.overflow = 'visible';
        el.style.overflowX = 'visible';
        el.style.overflowY = 'visible';
    });

    node.classList.add('dom-to-image-capture-mode');

    const originalNodeWidth = node.style.width;
    const { restore: restoreTableWidths, allCapped } = capPlanColumnWidths(node);
    // With every table pinned to its own width, the image crops to the tables instead of
    // carrying the container's empty space to the right of them.
    if (allCapped) node.style.width = 'fit-content';

    try {
        await new Promise((resolve) =>
            requestAnimationFrame(() => requestAnimationFrame(resolve))
        );

        const fullWidth = node.scrollWidth;
        const fullHeight = node.scrollHeight;

        const dataUrl = await domtoimage.toPng(node, {
            bgcolor: '#ffffff',
            width: fullWidth * scale,
            height: fullHeight * scale,
            style: {
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                width: `${fullWidth}px`,
                height: `${fullHeight}px`,
            },
        });

        // The PNG is captured at `scale` times the size for sharpness, so the email must show
        // it at the table's on-screen width or a narrow table renders `scale` times too large.
        return { base64: dataUrl.split(',')[1], width: fullWidth };
    } catch (error) {
        console.error('Error capturing image:', error);
        return null;
    } finally {
        node.classList.remove('dom-to-image-capture-mode');
        node.style.width = originalNodeWidth;
        restoreTableWidths();
        originalStyles.forEach(({ el, height, maxHeight, overflow, overflowX, overflowY }) => {
            el.style.height = height;
            el.style.maxHeight = maxHeight;
            el.style.overflow = overflow;
            el.style.overflowX = overflowX;
            el.style.overflowY = overflowY;
        });
    }
};

// containerId reads the table from another container than the Auto Quotation page's one for that label
export const getLiveTableHTML = (label, containerId = '') => {
    if (!containerId && label === 'Quotations') containerId = 'quotation-preview-container';
    if (!containerId && label === 'Table of benefits') containerId = 'tob-table-container';

    const container = document.getElementById(containerId);
    if (!container) return `[${label} Placeholder]`;

    const clone = container.cloneNode(true);

    // Remove unwanted elements
    const selectorsToRemove = [
        'button', 'input', '.MuiChip-root', '.MuiIconButton-root', '.MuiBadge-badge',
        'svg', '.MuiInputBase-root', '.dnd-context', '.MuiInput-root', '.MuiAutocomplete-root',
        '.remove-in-email'
    ];
    clone.querySelectorAll(selectorsToRemove.join(',')).forEach(el => el.remove());

    // Determine the plan (column) count for each table so we know when to bump up the font size
    // const planCountByTable = new Map();
    // clone.querySelectorAll('table').forEach(table => {
    //     const cols = table.querySelectorAll(':scope > colgroup > col');
    //     planCountByTable.set(table, Math.max(0, cols.length - 1));
    // });

    // Update styles and visibility
    clone.querySelectorAll('*').forEach(el => {
        // Show elements intended for email
        if (el.classList.contains('show-in-email')) {
            el.style.display = 'block';
        }

        // Clean up "Click to edit" without replacing innerHTML of parents
        if (el.children.length === 0 && el.textContent.includes('Click to edit')) {
            el.textContent = el.textContent.replace('Click to edit', '');
        }

        // Ensure tables look like tables in emails
        if (el.tagName === 'TABLE') {
            el.style.borderCollapse = 'collapse';
            el.style.width = '100%';
            // We remove table-level borders to allow individual row/cell border control via TD/TH
            el.style.border = 'none';
            el.setAttribute('border', '0');
        }
        if (el.tagName === 'TD' || el.tagName === 'TH') {
            const isNested = el.parentElement?.closest('td, th');
            const hasNoBorder = isNested ||
                el.classList.contains('remove-border-in-email') ||
                (el.parentElement && el.parentElement.classList.contains('remove-border-in-email'));

            el.style.border = hasNoBorder ? 'none' : '1px solid #d0d7e2';
            el.style.padding = isNested ? '0px' : '6px';
            // The editable TOB table outlines whichever cells are selected for formatting --
            // that outline is an editing affordance, not table content, so it never belongs in the email.
            el.style.boxShadow = 'none';

            if (el.classList.contains('remove-border-top-on-email') || (el.parentElement && el.parentElement.classList.contains('remove-border-top-on-email'))) {
                el.style.borderTop = 'none';
                el.style.paddingTop = '0px';
            }

            if (el.classList.contains('remove-border-bottom-on-email') || (el.parentElement && el.parentElement.classList.contains('remove-border-bottom-on-email'))) {
                el.style.borderBottom = 'none';
                el.style.paddingBottom = '0px';
            }

            // change font size
            // const parentTable = el.closest('table');
            // const planCount = parentTable ? planCountByTable.get(parentTable) : undefined;
            // if (planCount !== undefined && planCount <= 2) {
            //     el.style.fontSize = '16px';
            // }

        }
    });

    return clone.innerHTML;
};

export const EMAIL_STYLES = `
    div[label="Table of benefits"] td {
        border: 1px solid rgb(208, 215, 226);
        padding: 6px;
        font-size: 11px;
    }

    div[label="Table of benefits"] td.spacer-cell {
        border: none;
        padding: 0;
    }
    
    div[label="Quotations"] td {
        padding: 6px;
        font-size: 11px;
    }

    div[label="Quotations"] td table td {
        padding: 0px;
        font-size: 11px;
    }
`;

export const wrapEmailHtml = (content, styles) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    // Apply default font styles to the top-level div
    tempDiv.style.fontFamily = 'Calibri, Candara, Segoe, Segoe UI, Optima, Arial, sans-serif';
    tempDiv.style.fontSize = '12pt';

    // Apply min-width logic to each table for horizontal scrolling
    tempDiv.querySelectorAll('table').forEach(table => {
        // Skip nested tables to avoid applying large min-widths to layout tables inside cells
        if (table.parentElement && table.parentElement.closest('table')) {
            return;
        }

        let maxCols = 0;
        // Use table.rows to only count rows belonging to this specific table, ignoring nested ones
        Array.from(table.rows).forEach(row => {
            let colCount = 0;
            for (let i = 0; i < row.cells.length; i++) {
                colCount += row.cells[i].colSpan || 1;
            }
            if (colCount > maxCols) maxCols = colCount;
        });

        if (maxCols > 0) {
            let minWidth = 1000;
            if (maxCols > 5) {
                minWidth += (maxCols - 5) * 200;
            }
            table.style.minWidth = `${minWidth}px`;
        }
    });

    // The editor's Highlight writes <mark> tags, which Outlook doesn't style and drops the
    // background of, so each one goes out as a span carrying the same background colour.
    tempDiv.querySelectorAll('mark').forEach(mark => {
        const span = document.createElement('span');
        const color = mark.getAttribute('data-color') || mark.style.backgroundColor || '#ffff00';
        span.style.backgroundColor = color;
        span.append(...mark.childNodes);
        mark.replaceWith(span);
    });

    const placeholders = tempDiv.querySelectorAll('div[label="Table of benefits"], div[label="Quotations"]');
    placeholders.forEach(el => {
        const wrapper = document.createElement('div');
        wrapper.style.overflowX = 'auto';
        wrapper.style.webkitOverflowScrolling = 'touch';
        wrapper.style.width = '100%';

        // Wrap the element
        el.parentNode.insertBefore(wrapper, el);
        wrapper.appendChild(el);
    });

    return `
        <html>
            <head>
                <style>
                    ${styles}
                </style>
            </head>
            <body>
                <div style="font-family: Calibri, Candara, Segoe, Segoe UI, Optima, Arial, sans-serif; font-size: 12pt;">
                    ${tempDiv.innerHTML}
                </div>
            </body>
        </html>
    `;
};

const addTableSpacers = (html) => {
    if (!html) return html;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const table = tempDiv.querySelector('table');
    if (table) {
        const rows = Array.from(table.rows);
        if (rows.length > 41) {
            const spacerRow = document.createElement('tr');
            const spacerCell = document.createElement('td');
            spacerCell.classList.add('spacer-cell');
            spacerCell.colSpan = rows[0] ? rows[0].cells.length : 10;
            spacerCell.style.height = '250px';
            spacerCell.style.border = 'none';
            spacerCell.innerHTML = '&nbsp;';
            spacerRow.appendChild(spacerCell);

            // Insert after the 41st row (index 41 is the 42nd row, so insert before it)
            if (table.rows[41]) {
                table.rows[41].before(spacerRow);
            } else {
                table.appendChild(spacerRow);
            }
        }
    }
    return tempDiv.innerHTML;
};

const cleanTableStyles = (content) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const tobContainer = tempDiv.querySelector('div[label="Table of benefits"]');
    if (tobContainer) {
        tobContainer.querySelectorAll('td').forEach(td => {
            td.style.border = '';
            td.style.padding = '';
            td.style.fontSize = '';
            td.style.whiteSpace = '';
            td.style.wordBreak = '';
            td.style.cursor = '';
        });
    }

    const quotationContainer = tempDiv.querySelector('div[label="Quotations"]');
    if (quotationContainer) {
        quotationContainer.querySelectorAll('td').forEach(td => {
            td.style.padding = '';
            td.style.fontSize = '';
        });
    }

    return tempDiv.innerHTML;
};

export default function EmailFormDialog({ open, onClose, onSubmit, loading, initialValues, fromOptions: propFromOptions = [], ccOptions: propCcOptions = [], clientName, quotations = [], tobPlans = [] }) {
    const [formData, setFormData] = useState({
        from: '',
        to: '',
        cc: [],
        subject: '',
        body: ''
    });

    const [fromInput, setFromInput] = useState('');
    const [ccInput, setCcInput] = useState('');
    const [fetchedEmails, setFetchedEmails] = useState([]);
    const [isCapturing, setIsCapturing] = useState(false);
    const { auth } = useContext(AuthContext);
    const isInitialized = useRef(false);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    rel: 'noopener noreferrer',
                    target: '_blank',
                },
            }),
            TextStyle,
            Color,
            FontSize,
            PlaceholderNode,
            Highlight.configure({ multicolor: true }),
        ],
        content: '',
        onUpdate: ({ editor }) => {
            setFormData(prev => ({
                ...prev,
                body: editor.getHTML()
            }));
        },
    });

    useEffect(() => {
        const fetchEmails = async () => {
            if (!auth?.token) return;
            try {
                const response = await EmailDraftService.getEmails(auth.token);
                if (response.data.status === 'success') {
                    setFetchedEmails(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching emails:', error);
            }
        };

        fetchEmails();
    }, [auth?.token]);

    useEffect(() => {
        if (open) {
            if (!isInitialized.current) {
                let body = initialValues?.body || `
                    <p>Hi [Client Name],</p>
                    <p></p>
                    <p>I hope this message finds you well.</p>
                    <p></p>
                    <p>Please find the quotation details below for your reference:</p>
                    <div data-placeholder label="Table of benefits"></div>
                    <p></p>
                    <div data-placeholder label="Quotations"></div>
                    <p></p>
                    <p>For a clearer understanding of the coverage, kindly review the attached Summary of Benefits, Comparison Sheet, Customer Guide, and Policy Rules. These will walk you through the main features of each plan.</p>
                    <p></p>
                    <p>Should you have any questions or wish to discuss the options further, please feel free to let me know. I would be happy to assist you.</p>
                    <p></p>
                    <p>I look forward to hearing your thoughts.</p>
                    <p></p>
                    <p>Thank you.</p>
                    <p></p>
                    <p>Best Regards,</p>
                `;

                if (clientName) {
                    body = body.replace('[Client Name]', clientName);
                }

                setFormData({
                    from: initialValues?.from || auth?.user?.email || '',
                    to: initialValues?.to || '',
                    cc: Array.isArray(initialValues?.cc) ? initialValues.cc : (initialValues?.cc ? initialValues.cc.split(',').map(s => s.trim()).filter(Boolean) : []),
                    subject: initialValues?.subject || (clientName ? `Medical Insurance Quotation: ${clientName}` : 'Medical Insurance Quotation'),
                    body: body
                });
                if (editor) {
                    editor.commands.setContent(body);
                }
                setFromInput('');
                setCcInput('');
                isInitialized.current = true;
            } else if (editor) {
                // If already initialized and opening again, force refresh of placeholder previews
                const { state, view } = editor;
                const tr = state.tr;
                let hasPlaceholders = false;

                state.doc.descendants((node, pos) => {
                    if (node.type.name === 'placeholderNode') {
                        tr.setNodeMarkup(pos, undefined, {
                            ...node.attrs,
                            version: (node.attrs.version || 0) + 1
                        });
                        hasPlaceholders = true;
                    }
                });

                if (hasPlaceholders) {
                    view.dispatch(tr);
                }
            }
        }
    }, [open, initialValues, editor, clientName]);

    const fromOptions = (propFromOptions.length > 0 ? propFromOptions : fetchedEmails).filter(email => email !== formData.from);
    const ccOptions = (propCcOptions.length > 0 ? propCcOptions : fetchedEmails).filter(email => !formData.cc.includes(email));

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFromKeyDown = (event) => {
        if (event.key === ',') {
            event.preventDefault();
            const val = fromInput.trim();
            if (val) {
                if (isValidEmail(val)) {
                    setFormData(prev => ({ ...prev, from: val }));
                    setFromInput('');
                } else {
                    toast.error(`Invalid email format: ${val}`, { autoClose: 2000, hideProgressBar: true });
                }
            }
        }
    };

    const handleCcKeyDown = (event) => {
        if (event.key === ',') {
            event.preventDefault();
            const val = ccInput.trim();
            if (val) {
                if (isValidEmail(val)) {
                    setFormData(prev => {
                        if (!prev.cc.includes(val)) {
                            return { ...prev, cc: [...prev.cc, val] };
                        }
                        return prev;
                    });
                    setCcInput('');
                } else {
                    toast.error(`Invalid email format: ${val}`, { autoClose: 2000, hideProgressBar: true });
                }
            }
        }
    };

    const handleCreateDraft = async () => {
        if (!formData.from) {
            toast.error('The "From" field is required.', { autoClose: 2000, hideProgressBar: true });
            return;
        }
        if (formData.from && !isValidEmail(formData.from)) {
            toast.error('The "From" email is invalid.', { autoClose: 2000, hideProgressBar: true });
            return;
        }

        if (formData.to && !isValidEmail(formData.to)) {
            toast.error('The "To" email is invalid.', { autoClose: 2000, hideProgressBar: true });
            return;
        }

        if (!formData.subject) {
            toast.error('The "Subject" field is required.', { autoClose: 2000, hideProgressBar: true });
            return;
        }

        if (!formData.body || formData.body === '<p></p>') {
            toast.error('The "Body" field is required.', { autoClose: 2000, hideProgressBar: true });
            return;
        }

        const invalidCc = formData.cc.find(email => !isValidEmail(email));
        if (invalidCc) {
            toast.error(`Invalid CC email: ${invalidCc}`, { autoClose: 2000, hideProgressBar: true });
            return;
        }

        setIsCapturing(true);
        try {
            // The tables always go into the body as images; the HTML is only a fallback for a
            // failed capture.
            const quotationImage = await getLiveTableImage('Quotations');
            const tobImage = await getLiveTableImage('Table of benefits');

            // Final replacement of placeholders for the email body text
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = formData.body;
            const placeholders = tempDiv.querySelectorAll('div[data-placeholder]');
            placeholders.forEach(el => {
                const label = el.getAttribute('label');
                if (label === 'Quotations') {
                    if (quotationImage) {
                        el.innerHTML = `<img src="cid:quotation_image" alt="Quotation Table" width="${quotationImage.width}" style="width: ${quotationImage.width}px; max-width: 100%; height: auto;" />`;
                    } else {
                        el.innerHTML = getLiveTableHTML('Quotations');
                    }
                } else if (label === 'Table of benefits') {
                    if (tobImage) {
                        el.innerHTML = `<img src="cid:tob_image" alt="Table of Benefits" width="${tobImage.width}" style="width: ${tobImage.width}px; max-width: 100%; height: auto;" />`;
                    } else {
                        el.innerHTML = getLiveTableHTML('Table of benefits');
                    }
                } else {
                    el.innerHTML = `<p><strong>[${label}]</strong></p>`;
                }
            });

            onSubmit({
                ...formData,
                body: cleanTableStyles(tempDiv.innerHTML),
                cc: formData.cc.join(', '),
                quotation_image: quotationImage?.base64 ?? null,
                tob_image: tobImage?.base64 ?? null,
                // Keep the original HTML/Styles just in case the onSubmit/Backend still wants to generate PDF
                styles: EMAIL_STYLES,
                quotation_table_html: cleanTableStyles(`<div label="Quotations">${getLiveTableHTML('Quotations')}</div>`),
                tob_table_html: cleanTableStyles(`<div label="Table of benefits">${getLiveTableHTML('Table of benefits')}</div>`)
            });
        } catch (error) {
            console.error('Error creating draft:', error);
            toast.error('Failed to capture table images.');
        } finally {
            setIsCapturing(false);
        }
    };

    return (
        <Dialog
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 7,
                    py: 1.5,
                    px: 1.5,
                    backgroundColor: '#f1f4fa',
                },
            }}
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg">
            <DialogTitle sx={{ position: 'relative' }}>
                Compose Email
                <IconButton
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 12, top: 12, color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Autocomplete
                        multiple
                        freeSolo
                        options={fromOptions}
                        filterOptions={(options, params) => {
                            if (params.inputValue === '') {
                                return [];
                            }
                            return filter(options, params);
                        }}
                        value={formData.from ? [formData.from] : []}
                        inputValue={fromInput}
                        onInputChange={(event, newInputValue) => {
                            setFromInput(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                            const val = newValue.length > 0 ? newValue[newValue.length - 1] : '';
                            if (val === '' || isValidEmail(val)) {
                                setFormData(prev => ({ ...prev, from: val }));
                            } else {
                                toast.error(`Invalid email format: ${val}`, { autoClose: 2000, hideProgressBar: true });
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="From"
                                fullWidth
                                size="small"
                                margin="dense"
                                onKeyDown={handleFromKeyDown}
                            />
                        )}
                        sx={{ '& .MuiChip-root': { marginTop: '0 !important', marginBottom: '0 !important' } }}
                    />
                    <TextField
                        label="To"
                        name="to"
                        value={formData.to}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        margin="dense"
                    />
                    <Autocomplete
                        multiple
                        freeSolo
                        options={ccOptions}
                        filterOptions={(options, params) => {
                            if (params.inputValue === '') {
                                return [];
                            }
                            return filter(options, params);
                        }}
                        value={formData.cc}
                        inputValue={ccInput}
                        onInputChange={(event, newInputValue) => {
                            setCcInput(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                            const lastVal = newValue.length > 0 ? newValue[newValue.length - 1] : null;
                            if (lastVal && !isValidEmail(lastVal)) {
                                toast.error(`Invalid email format: ${lastVal}`, { autoClose: 2000, hideProgressBar: true });
                                return;
                            }
                            setFormData(prev => ({ ...prev, cc: newValue }));
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Cc"
                                fullWidth
                                size="small"
                                margin="dense"
                                onKeyDown={handleCcKeyDown}
                            />
                        )}
                        sx={{ '& .MuiChip-root': { marginTop: '0 !important', marginBottom: '0 !important' } }}
                    />
                    <TextField
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        margin="dense"
                    />
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', ml: 1.5 }}>Body</Typography>
                        <Box sx={{
                            mt: 0.5,
                            border: '1px solid #ccc',
                            borderRadius: 1,
                            '&:hover': {
                                borderColor: 'rgba(0, 0, 0, 0.87)'
                            },
                        }}>
                            <MenuBar editor={editor} />
                            <Box sx={{
                                '& .ProseMirror': {
                                    minHeight: '200px',
                                    outline: 'none',
                                    padding: '10px',
                                    backgroundColor: '#fff',
                                    fontFamily: 'Calibri, Candara, Segoe, Segoe UI, Optima, Arial, sans-serif',
                                    fontSize: '16px',
                                    '& p': { margin: '0 0 8px 0' },
                                    '& ul, & ol': { paddingLeft: '20px' },
                                    '& a': { color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' },
                                },
                            }}>
                                <EditorContent editor={editor} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={loading || isCapturing}>
                    Close
                </Button>
                <Button onClick={handleCreateDraft} variant="contained" disabled={loading || isCapturing} sx={{ backgroundColor: '#673ab7', '&:hover': { backgroundColor: '#512da8' } }}>
                    Create Draft
                    {(loading || isCapturing) && <CircularProgress size={16} sx={{ color: '#fff', ml: 1 }} />}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
